import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { WarningRounded as WarningIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import Big from 'big.js';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/BottomSheet';
import { useSetContentHeight } from '../../hooks';
import { formatTokenAmount } from '../../utils';
import { IconCircle, IconContainer } from './StatusBottomSheet.style';
export const ExchangeRateBottomSheet = forwardRef(({ onContinue, onCancel }, ref) => {
    const [data, setData] = useState();
    const bottomSheetRef = useRef(null);
    const resolverRef = useRef();
    const handleContinue = () => {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.close(true);
        onContinue === null || onContinue === void 0 ? void 0 : onContinue();
    };
    const handleCancel = useCallback(() => {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.close(false);
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }, [onCancel, ref]);
    const handleClose = useCallback(() => {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.close(false, false);
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }, [onCancel, ref]);
    useImperativeHandle(ref, () => ({
        isOpen: () => { var _a; return (_a = bottomSheetRef.current) === null || _a === void 0 ? void 0 : _a.isOpen(); },
        open: (resolver, data) => {
            var _a;
            setData(data);
            resolverRef.current = resolver;
            (_a = bottomSheetRef.current) === null || _a === void 0 ? void 0 : _a.open();
        },
        close: (value = false, bottomSheetClose = true) => {
            var _a, _b;
            (_a = resolverRef.current) === null || _a === void 0 ? void 0 : _a.call(resolverRef, value);
            if (bottomSheetClose) {
                (_b = bottomSheetRef.current) === null || _b === void 0 ? void 0 : _b.close();
            }
        },
    }), []);
    return (_jsx(BottomSheet, Object.assign({ ref: bottomSheetRef, onClose: handleClose }, { children: _jsx(ExchangeRateBottomSheetContent, { data: data, onContinue: handleContinue, onCancel: handleCancel }) })));
});
const ExchangeRateBottomSheetContent = ({ data, onCancel, onContinue }) => {
    const { t } = useTranslation();
    const ref = useRef();
    useSetContentHeight(ref);
    return (_jsxs(Box, Object.assign({ p: 3, ref: ref }, { children: [_jsxs(IconContainer, { children: [_jsx(IconCircle, Object.assign({ status: "warning", mb: 1 }, { children: _jsx(WarningIcon, { color: "warning" }) })), _jsx(Typography, Object.assign({ py: 1, fontSize: 18, fontWeight: 700 }, { children: t('swap.warning.title.rateChanged') }))] }), _jsx(Typography, Object.assign({ py: 1 }, { children: t('swap.warning.message.rateChanged') })), _jsxs(Box, Object.assign({ display: "flex", justifyContent: "space-between", mt: 1 }, { children: [_jsx(Typography, { children: t('swap.quotedAmount') }), _jsxs(Typography, Object.assign({ fontWeight: 600 }, { children: [t('format.number', {
                                value: formatTokenAmount(data === null || data === void 0 ? void 0 : data.oldToAmount, data === null || data === void 0 ? void 0 : data.toToken.decimals, 5),
                            }), ' ', data === null || data === void 0 ? void 0 : data.toToken.symbol] }))] })), _jsxs(Box, Object.assign({ display: "flex", justifyContent: "space-between", mt: 0.25 }, { children: [_jsx(Typography, { children: t('swap.currentAmount') }), _jsxs(Typography, Object.assign({ fontWeight: 600 }, { children: [t('format.number', {
                                value: formatTokenAmount(data === null || data === void 0 ? void 0 : data.newToAmount, data === null || data === void 0 ? void 0 : data.toToken.decimals, 5),
                            }), ' ', data === null || data === void 0 ? void 0 : data.toToken.symbol] }))] })), _jsxs(Box, Object.assign({ display: "flex", justifyContent: "space-between", mt: 0.25 }, { children: [_jsx(Typography, { children: t('swap.rateChange') }), _jsxs(Typography, Object.assign({ fontWeight: 600 }, { children: [Big((data === null || data === void 0 ? void 0 : data.newToAmount) || 0)
                                .div(Big((data === null || data === void 0 ? void 0 : data.oldToAmount) || 0))
                                .minus(1)
                                .mul(100)
                                .round(2, Big.roundUp)
                                .toString(), "%"] }))] })), _jsxs(Box, Object.assign({ display: "flex", mt: 3 }, { children: [_jsx(Button, Object.assign({ variant: "text", onClick: onCancel, fullWidth: true }, { children: t('button.cancel') })), _jsx(Box, { display: "flex", p: 1 }), _jsx(Button, Object.assign({ variant: "contained", onClick: onContinue, fullWidth: true }, { children: t('button.continue') }))] }))] })));
};
export const getTokenValueLossThreshold = (route) => {
    if (!route) {
        return false;
    }
    const fromAmountUSD = Big((route === null || route === void 0 ? void 0 : route.fromAmountUSD) || 0);
    const toAmountUSD = Big((route === null || route === void 0 ? void 0 : route.toAmountUSD) || 0);
    const gasCostUSD = Big((route === null || route === void 0 ? void 0 : route.gasCostUSD) || 0);
    if (fromAmountUSD.eq(0) && toAmountUSD.eq(0)) {
        return false;
    }
    return toAmountUSD.div(fromAmountUSD.plus(gasCostUSD)).lt(0.9);
};
